import { useRef, type CSSProperties } from 'react';

// The landing HUB — a two-door front page (Craft + Evaluate), AUTH-AWARE and reachable in both states
// (mirrors PSCraft's Landing model via props, not a router). Logged out: the doors + header lead to sign-in.
// Signed in: the doors lead INTO the two apps and the header offers "Go to your CVs" (app-home) — so a signed-in
// user returns here (via the wordmark) to switch between Craft and Evaluate. Its own header (wordmark +
// auth-aware button) IS the hub's chrome — the app header is not used on this view. Composes its OWN marketing
// copy — NOT a firewall surface (touches no CV-content path).

const TILES = [
  { h: 'Tailored to your specialty', p: 'Your CV, arranged to emphasize what your specialty actually values — so reviewers see the right things first.' },
  { h: 'Built for impact', p: 'CVCraft helps you turn what you did into clear, measurable results — drawing out the metrics and outcomes from your real experience, never padding or inventing them.' },
  // COPY CONTRACT (owner-endorsed, Sol-ratified, 2026-08-07) — see the block at the head of GATE_FALLBACKS in
  // workers/src/lib/classifierPrompt.ts. Marketing may not advertise a verdict the product DELIBERATELY DOES NOT
  // ISSUE. The read establishes what a CV COVERS (confirmed tagged entries) and what looks THIN; it never rates a
  // CV as "strong", and every in-product surface was corrected to stop implying it. The hero has to match.
  // Sol: "preserves the positive marketing promise without advertising a verdict the product does not issue."
  { h: 'An honest read of your CV', p: 'See what your CV already covers and where it looks thin — a free, honest read, before a program sees it.' },
];

export function Landing({ authed, onCraft, onEvaluate, onWorkspace, onSignIn }: {
  authed: boolean;
  onCraft: () => void;      // signed-in: into the Craft app
  onEvaluate: () => void;   // signed-in: into the Evaluate app
  onWorkspace: () => void;  // signed-in: header "Go to your CVs" → app-home
  // logged-out: reveal the sign-in form (all doors funnel here). Revenue P0 (P0-1) — the door buttons pass WHICH
  // door was clicked (door intent), so a successful sign-in can route straight there instead of a generic default;
  // the plain header "Sign in" passes none.
  onSignIn: (door?: 'craft' | 'evaluate') => void;
}) {
  const ctaRef = useRef<HTMLButtonElement>(null);
  // The wordmark is "home" on this single-page hub — scroll to the top and draw the eye to the primary CTA.
  function goHome() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    ctaRef.current?.focus();
  }
  return (
    <main style={L.page}>
      <header style={L.header}>
        <button style={L.brand} onClick={goHome} data-testid="landing-home">CVCraft</button>
        {authed ? (
          <button style={L.btnSecondary} onClick={onWorkspace} data-testid="nav-workspace">Go to your CVs</button>
        ) : (
          <button style={L.btnSecondary} onClick={() => onSignIn()} data-testid="nav-signin">Sign in</button>
        )}
      </header>

      <section style={L.hero}>
        <div style={L.heroInner}>
          <h1 style={L.h1}>Your residency CV, crafted for impact.</h1>
          <p style={L.subhead}>
            CVCraft guides CaRMS applicants section by section, with advice tailored to each specialty. You enter
            your experience and achievements; we shape how they're expressed into clear, results-focused entries —
            never inventing anything. Download a clean copy of your CV whenever you like.
          </p>
          <div style={L.ctaRow}>
            <button ref={ctaRef} style={L.btnPrimary} onClick={() => (authed ? onCraft() : onSignIn('craft'))} data-testid="landing-craft">Craft your CV</button>
            {/* Both primary doors are the navy theme color (was btnSecondary/gray — the two doors are peers, not primary/secondary). */}
            <button style={L.btnPrimary} onClick={() => (authed ? onEvaluate() : onSignIn('evaluate'))} data-testid="landing-review">Review your CV</button>
          </div>
          <p style={L.subCta}>Build it step by step, or paste a CV you've already started for a free read.</p>
        </div>

        <ul style={L.tiles}>
          {TILES.map((t) => (
            <li key={t.h} style={L.card}>
              <h3 style={L.cardH}>{t.h}</h3>
              <p style={L.cardP}>{t.p}</p>
            </li>
          ))}
        </ul>
      </section>

      <footer style={L.footer}>
        <div style={L.footerInner}>
          <p style={L.disclaimer}>CVCraft is independent and not affiliated with CaRMS, AFMC, or any university residency program.</p>
          {/* Polish B2 — the Help stub becomes a real link, alongside the new Refund Policy link. Same pattern as
              Privacy/Terms (Trust/Legal P1): a plain full-page navigation is fine — the SPA fallback serves
              index.html for any path, and App's boot-time pathname check renders the right view immediately. */}
          <nav style={L.footerNav} aria-label="Footer">
            <a style={L.footerLink} href="/help" data-testid="footer-help">Help</a>
            <a style={L.footerLink} href="/privacy" data-testid="footer-privacy">Privacy</a>
            <a style={L.footerLink} href="/terms" data-testid="footer-terms">Terms</a>
            <a style={L.footerLink} href="/refunds" data-testid="footer-refunds">Refund Policy</a>
          </nav>
        </div>
      </footer>
    </main>
  );
}

const L: Record<string, CSSProperties> = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--sans)', color: 'var(--ink)' },
  header: { borderBottom: '1px solid var(--rule)', background: 'var(--card)', padding: '0 var(--space-5)', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand: { fontFamily: 'var(--serif)', fontSize: 20, color: 'var(--ink)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' },
  hero: { flex: 1, padding: 'var(--space-8) var(--space-5)' },
  heroInner: { margin: '0 auto', maxWidth: 640, textAlign: 'center' },
  h1: { fontFamily: 'var(--serif)', fontSize: 'var(--fs-display)', lineHeight: 'var(--lh-tight)', margin: 0, color: 'var(--ink)' },
  subhead: { marginTop: 'var(--space-5)', fontSize: 'var(--fs-h2)', lineHeight: 'var(--lh-prose)', color: 'var(--ink-subtle)' },
  ctaRow: { marginTop: 'var(--space-6)', display: 'flex', gap: 'var(--space-3)', justifyContent: 'center', flexWrap: 'wrap' },
  btnPrimary: { background: 'var(--ink)', color: '#fff', border: '1px solid var(--ink)', borderRadius: 6, padding: 'var(--space-3) var(--space-4)', fontSize: 15, cursor: 'pointer' },
  btnSecondary: { background: 'var(--card)', color: 'var(--ink)', border: '1px solid var(--rule)', borderRadius: 6, padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--fs-body)', cursor: 'pointer' },
  subCta: { marginTop: 'var(--space-3)', fontSize: 'var(--fs-label)', color: 'var(--muted)' },
  tiles: { marginTop: 'var(--space-8)', marginLeft: 'auto', marginRight: 'auto', maxWidth: 760, display: 'grid', gap: 'var(--space-5)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', listStyle: 'none', padding: 0 },
  card: { background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 10, padding: 'var(--space-5)', textAlign: 'center' },
  cardH: { fontFamily: 'var(--serif)', fontSize: 'var(--fs-h3)', fontWeight: 600, margin: '0 0 var(--space-2)', color: 'var(--ink)' },
  cardP: { fontSize: 'var(--fs-body)', lineHeight: 'var(--lh-prose)', color: 'var(--ink-subtle)', margin: 0 },
  footer: { borderTop: '1px solid var(--rule)', padding: 'var(--space-4) var(--space-5)', fontSize: 'var(--fs-label)', color: 'var(--muted)' },
  footerInner: { margin: '0 auto', maxWidth: 760, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' },
  disclaimer: { margin: 0 },
  footerNav: { display: 'flex', gap: 'var(--space-4)' },
  footerLink: { color: 'var(--muted)' },
};
